import React, { useEffect, useState } from 'react';
import { Box, Paper, Button, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TextField, MenuItem, IconButton, Autocomplete } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

// Placeholder types
interface Specimen {
  id: number;
  accession_number: string;
  patient_name: string;
  patient_dob: string;
  type: string;
  source: string;
  status: string;
  collected_at: string;
  received_at: string;
}

interface PaginatedSpecimens {
  specimens: Specimen[];
  total: number;
  page: number;
  pageSize: number;
}

const AccessioningDashboard: React.FC = () => {
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedSpecimen, setSelectedSpecimen] = useState<Specimen | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [testTemplates, setTestTemplates] = useState<any[]>([]);
  const [testTemplatesLoading, setTestTemplatesLoading] = useState(false);
  const [apCaseTemplates, setApCaseTemplates] = useState<any[]>([]);
  const [apCaseTemplateLoading, setApCaseTemplateLoading] = useState(false);
  const [selectedApCaseTemplate, setSelectedApCaseTemplate] = useState('');
  const [specimenClasses, setSpecimenClasses] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [specimenParts, setSpecimenParts] = useState<any[]>([]);
  const [specimenPartsLoading, setSpecimenPartsLoading] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  const orderStatusOptions = ['pending', 'in progress', 'completed'];

  const initialSpecimenForm = {
    accession_number: '',
    patient_id: '',
    status: 'pending',
    source: '',
    collected_at: '',
    received_at: '',
  };

  const initialOrder = { template_id: '', status: 'pending' };

  const [specimenForm, setSpecimenForm] = useState<typeof initialSpecimenForm>({ ...initialSpecimenForm });
  const [orders, setOrders] = useState<any[]>([{ ...initialOrder }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Orders for selected specimen ---
  const [ordersForSpecimen, setOrdersForSpecimen] = useState<any[]>([]);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const fetchSpecimens = async (pageNum = 0, pageSize = 10) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/specimens?page=${pageNum + 1}&limit=${pageSize}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch specimens');
      const data: PaginatedSpecimens = await res.json();
      setSpecimens(data.specimens || []);
      setTotal(data.total || 0);
    } catch (e) {
      setSpecimens([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    fetchSpecimens(page, rowsPerPage);
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectSpecimen = (specimen: Specimen) => {
    setSelectedSpecimen(specimen);
    setShowNewForm(false);
  };

  const handleSpecimenFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setSpecimenForm(prev => ({ ...prev, [name]: value }));
  };

  const handleOrderChange = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setOrders(prev => {
      const newOrders = [...prev];
      newOrders[index][name] = value;
      return newOrders;
    });
  };

  const handleAddOrder = () => {
    setOrders(prev => [...prev, { ...initialOrder }]);
  };

  const handleRemoveOrder = (index: number) => {
    setOrders(prev => {
      const newOrders = prev.filter((_, i) => i !== index);
      return newOrders;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('specimen', JSON.stringify(specimenForm));
      formData.append('ap_case_template_id', selectedApCaseTemplate);
      formData.append('orders', JSON.stringify(orders));
      attachments.forEach((file, idx) => {
        formData.append('attachments', file);
      });
      const res = await fetch('/api/specimens', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // 'Content-Type' is omitted for FormData
        },
        body: formData,
      });
      if (!res.ok) throw new Error('Failed to save specimen');
      const data = await res.json();
      setSpecimens(prev => [...prev, data.specimen]);
      setTotal(prev => prev + 1);
      setShowNewForm(false);
      setSpecimenForm({ ...initialSpecimenForm });
      setOrders([{ ...initialOrder }]);
      setAttachments([]);
      setError(null);
    } catch (e) {
      setError('An error occurred while saving the specimen');
    } finally {
      setSaving(false);
    }
  };

  const fetchOrdersForSpecimen = async (specimenId: number, pageNum = 0, pageSize = 10) => {
    setOrdersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/orders?specimen_id=${specimenId}&page=${pageNum + 1}&limit=${pageSize}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch orders');
      const data = await res.json();
      setOrdersForSpecimen(data.orders || []);
      setOrdersTotal(data.total || 0);
    } catch (e) {
      setOrdersForSpecimen([]);
      setOrdersTotal(0);
    }
    setOrdersLoading(false);
  };

  useEffect(() => {
    if (selectedSpecimen) {
      fetchOrdersForSpecimen(selectedSpecimen.id, ordersPage, ordersRowsPerPage);
    }
    // eslint-disable-next-line
  }, [selectedSpecimen, ordersPage, ordersRowsPerPage]);

  const handleOrdersChangePage = (_: unknown, newPage: number) => {
    setOrdersPage(newPage);
  };

  const handleOrdersChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOrdersRowsPerPage(parseInt(event.target.value, 10));
    setOrdersPage(0);
  };

  // Fetch specimen classes on mount
  useEffect(() => {
    fetch('/api/specimen-classes')
      .then(res => res.json())
      .then(data => setSpecimenClasses(data))
      .catch(() => setSpecimenClasses([]));
  }, []);

  // Fetch patients on mount
  useEffect(() => {
    setPatientsLoading(true);
    fetch('/api/patients?page=1&limit=1000', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    })
      .then(res => res.json())
      .then(data => setPatients(data.patients || []))
      .catch(() => setPatients([]))
      .finally(() => setPatientsLoading(false));
  }, []);

  // Fetch specimen parts when specimen class changes
  useEffect(() => {
    if (selectedClassId) {
      setSpecimenPartsLoading(true);
      fetch(`/api/specimen-classes/${selectedClassId}/parts`)
        .then(res => res.json())
        .then(data => setSpecimenParts(data))
        .catch(() => setSpecimenParts([]))
        .finally(() => setSpecimenPartsLoading(false));
    } else {
      setSpecimenParts([]);
    }
  }, [selectedClassId]);

  // Update test templates and AP case templates fetches to use selectedClassId
  useEffect(() => {
    if (selectedClassId) {
      setTestTemplatesLoading(true);
      fetch(`/api/test-templates/for-specimen-class/${selectedClassId}`)
        .then(res => res.json())
        .then(data => setTestTemplates(data))
        .catch(() => setTestTemplates([]))
        .finally(() => setTestTemplatesLoading(false));
      setOrders(prev => prev.map(order => ({ ...order, template_id: '' })));
    } else {
      setTestTemplates([]);
      setOrders(prev => prev.map(order => ({ ...order, template_id: '' })));
    }
  }, [selectedClassId]);

  useEffect(() => {
    if (selectedClassId) {
      setApCaseTemplateLoading(true);
      fetch(`/api/ap-case-templates/for-specimen-class/${selectedClassId}`)
        .then(res => res.json())
        .then(data => setApCaseTemplates(data))
        .catch(() => setApCaseTemplates([]))
        .finally(() => setApCaseTemplateLoading(false));
      setSelectedApCaseTemplate('');
    } else {
      setApCaseTemplates([]);
      setSelectedApCaseTemplate('');
    }
  }, [selectedClassId]);

  return (
    <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
      {/* Left Panel: Specimen List */}
      <Paper sx={{ width: 350, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Specimens</Typography>
          <Button variant="contained" onClick={() => { setShowNewForm(true); setSelectedSpecimen(null); }}>New</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Patient</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {specimens.map((specimen) => (
                <TableRow
                  key={specimen.id}
                  hover
                  selected={selectedSpecimen?.id === specimen.id}
                  onClick={() => handleSelectSpecimen(specimen)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{specimen.id}</TableCell>
                  <TableCell>{specimen.patient_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Right Panel: Details or New Form */}
      <Paper sx={{ flex: 1, p: 2 }}>
        {showNewForm ? (
          <>
            <Typography variant="h6">New Specimen & Orders</Typography>
            <Divider sx={{ my: 2 }} />
            {/* Specimen Class Dropdown */}
            <TextField
              select
              label="Specimen Class"
              value={selectedClassId ?? ''}
              onChange={e => {
                const id = Number(e.target.value);
                setSelectedClassId(id);
                const classConfig = specimenClasses.find(cls => cls.id === id);
                // Reset all relevant state when specimen class changes
                setSpecimenForm(prev => ({ 
                  ...initialSpecimenForm,
                  type: classConfig ? classConfig.label : ''
                }));
                setOrders([{ ...initialOrder }]);
                setSelectedApCaseTemplate('');
                setTestTemplates([]);
                setApCaseTemplates([]);
                setSpecimenParts([]);
              }}
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select Class</MenuItem>
              {specimenClasses.map(cls => (
                <MenuItem key={cls.id} value={cls.id}>{cls.label}</MenuItem>
              ))}
            </TextField>
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
              <TextField label="Accession #" name="accession_number" value={specimenForm.accession_number} onChange={handleSpecimenFormChange} required fullWidth />
              <Autocomplete
                options={patients}
                getOptionLabel={option => `${option.name} (DOB: ${option.dob})`}
                loading={patientsLoading}
                value={patients.find(p => p.id === specimenForm.patient_id) || null}
                onChange={(_, value) => setSpecimenForm(prev => ({ ...prev, patient_id: value ? value.id : '' }))}
                renderInput={params => (
                  <TextField {...params} label="Patient Name" required fullWidth />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <TextField
                select
                label="Source"
                name="source"
                value={specimenForm.source}
                onChange={handleSpecimenFormChange}
                fullWidth
                required
                disabled={specimenPartsLoading || !selectedClassId}
              >
                <MenuItem value="">{specimenPartsLoading ? 'Loading...' : 'Select Source'}</MenuItem>
                {specimenParts.map(part => (
                  <MenuItem key={part.id} value={part.part_name}>{part.part_name}</MenuItem>
                ))}
              </TextField>
              <TextField label="Status" name="status" select value={specimenForm.status} onChange={handleSpecimenFormChange} fullWidth>
                {orderStatusOptions.map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
              </TextField>
              <TextField label="Collected At" name="collected_at" type="datetime-local" value={specimenForm.collected_at} onChange={handleSpecimenFormChange} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Received At" name="received_at" type="datetime-local" value={specimenForm.received_at} onChange={handleSpecimenFormChange} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField
                select
                label="Case Template"
                value={selectedApCaseTemplate}
                onChange={e => setSelectedApCaseTemplate(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                disabled={apCaseTemplateLoading || !selectedClassId}
              >
                <MenuItem value="">{apCaseTemplateLoading ? 'Loading...' : 'Select Case Template'}</MenuItem>
                {apCaseTemplates.map(tpl => (
                  <MenuItem key={tpl.id} value={tpl.id}>{tpl.name}</MenuItem>
                ))}
              </TextField>
              <Box>
                <Typography variant="subtitle2">Attachments</Typography>
                <input
                  type="file"
                  multiple
                  onChange={e => setAttachments(e.target.files ? Array.from(e.target.files) : [])}
                  style={{ display: 'block', width: '100%' }}
                />
                {attachments.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {attachments.map((file, idx) => (
                      <li key={idx}>{file.name}</li>
                    ))}
                  </ul>
                )}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">Orders</Typography>
              {orders.map((order, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    select
                    label="Template/Test ID"
                    name="template_id"
                    value={order.template_id}
                    onChange={e => handleOrderChange(idx, e)}
                    required
                    sx={{ flex: 2 }}
                    disabled={testTemplatesLoading || !selectedClassId}
                  >
                    <MenuItem value="">{testTemplatesLoading ? 'Loading...' : 'Select Test Template'}</MenuItem>
                    {testTemplates.map(tt => (
                      <MenuItem key={tt.id} value={tt.id}>{tt.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Status" name="status" select value={order.status} onChange={e => handleOrderChange(idx, e)} sx={{ flex: 1 }}>
                    {orderStatusOptions.map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                  </TextField>
                  <IconButton onClick={() => handleRemoveOrder(idx)} disabled={orders.length === 1}><Delete /></IconButton>
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={handleAddOrder}>Add Order</Button>
              {error && <Typography color="error">{error}</Typography>}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
                <Button onClick={() => { setShowNewForm(false); setSpecimenForm({ ...initialSpecimenForm }); setOrders([{ ...initialOrder }]); setError(null); }}>Cancel</Button>
              </Box>
            </Box>
          </>
        ) : selectedSpecimen ? (
          <>
            <Typography variant="h6">Specimen Details</Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography><b>Accession #:</b> {selectedSpecimen.accession_number}</Typography>
              <Typography><b>Patient Name:</b> {selectedSpecimen.patient_name}</Typography>
              <Typography><b>DOB:</b> {selectedSpecimen.patient_dob}</Typography>
              <Typography><b>Type:</b> {selectedSpecimen.type}</Typography>
              <Typography><b>Source:</b> {selectedSpecimen.source}</Typography>
              <Typography><b>Status:</b> {selectedSpecimen.status}</Typography>
              <Typography><b>Collected At:</b> {selectedSpecimen.collected_at}</Typography>
              <Typography><b>Received At:</b> {selectedSpecimen.received_at}</Typography>
            </Box>
            <Typography variant="subtitle1">Orders</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Template ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersForSpecimen.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.id}</TableCell>
                      <TableCell>{order.template_id}</TableCell>
                      <TableCell>{order.status}</TableCell>
                      <TableCell>{order.created_at ? new Date(order.created_at).toLocaleString() : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={ordersTotal}
              page={ordersPage}
              onPageChange={handleOrdersChangePage}
              rowsPerPage={ordersRowsPerPage}
              onRowsPerPageChange={handleOrdersChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">Select a specimen or create a new one.</Typography>
        )}
      </Paper>
    </Box>
  );
};

export default AccessioningDashboard; 